import React, {useState} from 'react';
import {Platform, Pressable, StyleSheet, Text, View} from 'react-native';

// Deliberately uses only core native views. There is no executable SVG/HTML,
// WebView, remote resource, new native module or platform-specific renderer.
function Choice({label, selected, disabled, onPress, colors}) {
  return <Pressable accessibilityRole="radio" accessibilityLabel={label}
    accessibilityState={{checked:selected,disabled}} aria-checked={selected}
    disabled={disabled} onPress={onPress}
    style={[styles.choice,{backgroundColor:selected?colors.primary:colors.background,borderColor:selected?colors.primary:colors.border}]}>
    <Text style={{color:selected?colors.background:colors.primary,fontSize:13,fontWeight:'500'}}>{label}</Text>
  </Pressable>;
}

function Shape({shape, scale, color}) {
  const stroke=(shape.strokeWidth || 3)*scale;
  if(shape.type==='line') return shape.points.slice(1).map((end,index)=>{
    const start=shape.points[index], dx=(end[0]-start[0])*scale, dy=(end[1]-start[1])*scale;
    const length=Math.hypot(dx,dy);
    return <View key={index} style={{position:'absolute',left:(start[0]+end[0])/2*scale-length/2,top:(start[1]+end[1])/2*scale-stroke/2,
      width:length,height:stroke,borderRadius:stroke/2,backgroundColor:color,transform:[{rotate:`${Math.atan2(dy,dx)}rad`}]}} />;
  });
  const circle=shape.type==='circle';
  return <View style={{position:'absolute',left:(circle?shape.cx-shape.r:shape.x)*scale,top:(circle?shape.cy-shape.r:shape.y)*scale,
    width:(circle?shape.r*2:shape.width)*scale,height:(circle?shape.r*2:shape.height)*scale,
    borderWidth:stroke,borderRadius:circle?shape.r*scale:3*scale,borderColor:color,backgroundColor:shape.filled?color:'transparent'}} />;
}

export default function AnswerDiagram({node,values,update,ready,colors}) {
  const [width,setWidth]=useState(600);
  const scale=width/1000;
  const selected=node.parts.find(part=>part.label===values[node.bind]);
  const amount=node.explodeBind?values[node.explodeBind]:0;
  const setAmount=value=>update(node.explodeBind,Math.max(0,Math.min(1,Number(value.toFixed(2)))));
  const tones={ink:colors.primary,muted:colors.secondary,accent:colors.primary};
  return <View style={styles.container}>
    <Text style={[styles.heading,{color:colors.primary}]}>{node.label}</Text>
    <View accessibilityRole="image" accessibilityLabel={`${node.label}. ${selected?selected.label:'All parts'}. ${amount>0?'Separated':'Assembled'} view.`}
      onLayout={event=>{const next=event.nativeEvent.layout.width;if(next>0&&next!==width)setWidth(next);}}
      style={[styles.canvas,{backgroundColor:colors.band}]}>
      {node.parts.map(part=><View key={part.id} pointerEvents="none" style={[StyleSheet.absoluteFillObject,
        {opacity:!selected||selected.id===part.id?1:0.16,transform:[{translateX:(part.offset?.[0]||0)*amount*scale},{translateY:(part.offset?.[1]||0)*amount*scale}]}]}>
        {part.shapes.map((shape,index)=><Shape key={index} shape={shape} scale={scale} color={tones[shape.tone||'ink']} />)}
      </View>)}
      <View pointerEvents="none" style={styles.badge}><Text style={{fontSize:11,color:colors.secondary}}>{selected?.label||'All parts'} · Simplified diagram</Text></View>
    </View>
    <View accessibilityRole="radiogroup" accessibilityLabel={`${node.label} parts`} style={styles.choices}>
      {['All',...node.parts.map(part=>part.label)].map(label=><Choice key={label} label={label} selected={values[node.bind]===label} disabled={!ready} colors={colors} onPress={()=>update(node.bind,label)} />)}
    </View>
    {node.explodeBind&&<View style={styles.explosion}>
      <View style={styles.explosionHeading}><Text style={{color:colors.secondary,fontSize:12}}>Exploded view</Text><Text style={{color:colors.secondary,fontSize:12}}>{Math.round(amount*100)}%</Text></View>
      {Platform.OS==='web'?<input aria-label={`${node.label} separation`} type="range" min="0" max="1" step="0.01" value={amount} disabled={!ready}
        onChange={event=>setAmount(Number(event.target.value))} style={{width:'100%',accentColor:colors.primary,margin:0}} />
        :<View style={styles.choices}>{[['Assembled',0],['Half separated',0.5],['Fully separated',1]].map(([label,value])=><Choice key={label} label={label} selected={amount===value} disabled={!ready} colors={colors} onPress={()=>setAmount(value)}/>)}</View>}
    </View>}
    <View accessibilityLiveRegion="polite" style={[styles.description,{borderColor:colors.border}]}>
      <Text style={[styles.heading,{color:colors.primary}]}>{selected?.label||'Explore the parts'}</Text>
      <Text style={{color:colors.secondary,fontSize:14,lineHeight:22}}>{selected?.description||'Choose a part to highlight it and learn how it fits into the whole.'}</Text>
    </View>
  </View>;
}
const styles=StyleSheet.create({
  container:{gap:16,minWidth:0},heading:{fontSize:16,fontWeight:'600',lineHeight:23},
  canvas:{width:'100%',aspectRatio:5/3,borderRadius:16,overflow:'hidden'},
  badge:{position:'absolute',left:12,bottom:10},
  choices:{flexDirection:'row',flexWrap:'wrap',gap:6},choice:{minHeight:44,paddingHorizontal:13,paddingVertical:10,borderRadius:22,borderWidth:1,justifyContent:'center'},
  explosion:{gap:10},explosionHeading:{flexDirection:'row',justifyContent:'space-between'},
  description:{borderTopWidth:1,paddingTop:16,gap:6},
});
